import { parseDateValue } from '@/lib/utils';
import type { StatCardTone } from '@/components/ui/StatCard';
import type { BadgeVariant, SelectOption } from '@/types/common.types';
import type { FilterConfig } from '@/types/filters.types';
import type { ManagementSearchType, MembershipManagementRow, MembershipManagementStats } from '@/services/memberships-management.service';

export type MembershipManagementMode = 'memberships' | 'members';

/**
 * Filter keys MUST match the canonical legacy filter registry.
 * DO NOT rename keys or change option values — dynamic lists store these permanently.
 * See lib/filterRegistry.ts for the full registry and rules.
 */
export const MEMBERSHIP_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search memberships...' },
  { key: 'daterange', label: 'Date', type: 'daterange' },
  { key: 'membership-type', label: 'Membership', type: 'select', allLabel: 'All memberships', multi: true },
  { key: 'membership-status', label: 'Status', type: 'select', allLabel: 'All statuses', multi: true },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
];

export const MEMBERSHIP_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  active: 'success',
  added: 'info',
  declined: 'error',
  cancelled: 'error',
  cancelling: 'error',
  frozen: 'info',
};

export const MEMBERSHIP_SEARCH_OPTIONS: SelectOption<ManagementSearchType>[] = [
  { value: '0', label: 'Name' },
  { value: '1', label: 'Plan ID' },
  { value: '2', label: 'Patient ID' },
  { value: '5', label: 'Pymt. ID' },
  { value: '3', label: 'Amount' },
  { value: '4', label: 'User' },
];

/**
 * Re-exported, not redeclared.
 *
 * This was a verbatim copy of filterRegistry's MEMBERSHIP_STATUS_INDEX — same
 * five indices, same five labels — typed as
 * `SelectOption<MembershipFilterStatus>[]`, which is the wrong vocabulary
 * entirely: MembershipFilterStatus is words ('added', 'declined', …) while
 * these values are the legacy numeric indices, as the deep-link map below says
 * in so many words. tsc had been reporting all five as unassignable.
 *
 * The registry is the canonical copy and carries "NEVER reorder, remove, or
 * change these mappings" above it, which is exactly the kind of table that
 * should not exist twice. Both importers of this name keep working.
 */
export { MEMBERSHIP_STATUS_OPTIONS } from '@/lib/filterRegistry';

/**
 * The `?status=` tokens /memberships/all accepts as a deep link, mapped to the
 * `membership-status` values each one seeds.
 *
 * Keys are row statuses (the vocabulary `MembershipFilterStatus` and the status
 * badges already speak); values are the legacy filter indices from
 * MEMBERSHIP_STATUS_OPTIONS. 'active' is absent because the filter has no option
 * for it.
 *
 * 'cancelled' deliberately covers BOTH Cancelled and Set to Cancel, so that
 * "View All" on the dashboard's Recent Cancellations card lands on exactly the
 * rows that card previews — it builds its own query from the same two indices.
 */
export const MEMBERSHIP_STATUS_DEEP_LINKS: Record<string, string[]> = {
  added: ['0'],
  declined: ['1'],
  cancelled: ['2', '4'],
  frozen: ['3'],
  cancelling: ['4'],
};

/** The `membership-status` values a `?status=` token asks for, or null if it names nothing. */
export function membershipStatusDeepLink(token: string | null | undefined): string[] | null {
  if (!token) return null;
  // Array.isArray, not ?? null: an arbitrary URL token also reaches inherited
  // Object keys, and `?status=constructor` must read as unrecognised too.
  const seed = MEMBERSHIP_STATUS_DEEP_LINKS[token.toLowerCase()];
  return Array.isArray(seed) ? seed : null;
}

export const EMPTY_MEMBERSHIP_STATS: MembershipManagementStats = {
  totalCount: 0,
  activeCount: 0,
  addedCount: 0,
  cancelledCount: 0,
  frozenCount: 0,
  declinedCount: 0,
};

/**
 * The five numbers legacy shows above the list, in legacy's order
 * (`$totalStatsCount` = total;added;cancelled;frozen;past due).
 * Only the first label differs between the two tabs.
 */
export function membershipStatTiles(
  mode: MembershipManagementMode,
  stats: MembershipManagementStats,
): { key: string; label: string; value: number; tone: StatCardTone }[] {
  return [
    {
      key: 'total',
      label: mode === 'memberships' ? 'Memberships' : 'Members',
      value: stats.totalCount,
      tone: 'blue',
    },
    { key: 'added', label: 'Added', value: stats.addedCount, tone: 'emerald' },
    { key: 'cancelled', label: 'Cancelled', value: stats.cancelledCount, tone: 'red' },
    { key: 'frozen', label: 'Frozen', value: stats.frozenCount, tone: 'sky' },
    { key: 'declined', label: 'Past Due', value: stats.declinedCount, tone: 'amber' },
  ];
}

// parseDateValue, not a bare `new Date()`: Next Charge arrives as a plain 'YYYY-MM-DD'
// calendar day, which `new Date()` reads as UTC midnight — the day before in US zones.
export function formatMembershipDate(value: string | null | undefined): { monthDay: string; year: string } | null {
  const date = parseDateValue(value);
  if (!date) return null;
  return {
    monthDay: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    year: String(date.getFullYear()),
  };
}

export function formatMembershipDateInline(value: string | null | undefined): string {
  const date = parseDateValue(value);
  if (!date) return '\u2014';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatMembershipCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

export function membershipStatusLabel(status: string): string {
  switch (status) {
    case 'declined':
      return 'Past Due';
    case 'cancelled':
      return 'Cancelled';
    case 'cancelling':
      return 'Set to Cancel';
    case 'frozen':
      return 'Frozen';
    default:
      return 'Added';
  }
}

export function membershipRowClass(row: MembershipManagementRow): string {
  if (row.status === 'cancelled') return 'text-[#a3a3a3]';
  if (row.status === 'declined') return 'text-[#990000]';
  if (row.status === 'frozen') return 'bg-blue-50';
  return '';
}

export function membershipPageTitle(mode: MembershipManagementMode): string {
  return mode === 'memberships' ? 'All Memberships' : 'Members';
}

export function membershipAIContextTitle(mode: MembershipManagementMode): string {
  return mode === 'memberships' ? 'All Memberships' : 'Membership Members';
}
